import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const salesByPlatform = [
  { platform: 'Apple', revenue: 4200, units: 420 },
  { platform: 'Google', revenue: 3100, units: 310 },
  { platform: 'Spotify', revenue: 2800, units: 280 },
  { platform: 'SFTP', revenue: 2245, units: 224 },
];

const topTitles = [
  { isbn: '978-2-123-456', title: 'Le Petit Prince', units: 234, revenue: 2340 },
  { isbn: '978-2-789-012', title: 'Guide voyage', units: 189, revenue: 1890 },
  { isbn: '978-2-345-678', title: 'Manuel technique', units: 156, revenue: 1560 },
  { isbn: '978-2-901-234', title: 'Recettes', units: 143, revenue: 1430 },
];

export default function DistSalesPage() {
  const [period, setPeriod] = useState('month');

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Sales Reports</h1>
          <p className="text-slate-500 mt-1">Track revenue and sales performance</p>
        </div>
        <div className="flex gap-3">
          <Select
            options={[
              { value: 'day', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'year', label: 'This Year' },
            ]}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-40"
          />
          <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-4xl font-bold text-slate-800">$12,345</p>
            <p className="text-slate-500 mt-1">Total Revenue</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-4xl font-bold text-slate-800">1,234</p>
            <p className="text-slate-500 mt-1">Units Sold</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-4xl font-bold text-slate-800">89</p>
            <p className="text-slate-500 mt-1">Active Titles</p>
          </CardBody>
        </Card>
      </div>

      {/* Revenue by Platform Chart */}
      <Card>
        <CardHeader title="Revenue by Platform" />
        <CardBody>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesByPlatform}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="platform" stroke="#94A3B8" />
                <YAxis stroke="#94A3B8" />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                <Legend />
                <Bar dataKey="revenue" fill="#4AC3C3" name="Revenue ($)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="units" fill="#F5C542" name="Units" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      {/* Top Titles */}
      <Card>
        <CardHeader title="Top Selling Titles" />
        <CardBody className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">ISBN</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Title</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Units</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {topTitles.map((title) => (
                <tr key={title.isbn} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-sm">{title.isbn}</td>
                  <td className="px-4 py-3 font-medium">{title.title}</td>
                  <td className="px-4 py-3 text-right">{title.units}</td>
                  <td className="px-4 py-3 text-right font-medium">${title.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

